import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion/dist/framer-motion";
import { useGame } from "../GameState";
import { SFX, resumeAudio } from "../sfx";
import HUD, { AchievementPopup } from "../components/HUD";
import GameControls from "../components/GameControls";
import { GALLERY_PHOTOS } from "../photoManifest";

const PUZZLE_PHOTOS = [
  process.env.PUBLIC_URL + "/mothersday/family-yellow.jpg",
  process.env.PUBLIC_URL + "/mothersday/family-pink.jpg",
  GALLERY_PHOTOS[0] || (process.env.PUBLIC_URL + "/mothersday/family-yellow.jpg"),
];

const SIZE = 3;            // 3x3
const TILES = SIZE * SIZE;
const TIME_BONUS_BASE = 300;
const MOVE_PENALTY = 5;

const idxToXY = (i) => [i % SIZE, Math.floor(i / SIZE)];

const isSolved = (board) => board.every((v, i) => v === i);

const isSolvable = (board) => {
  // For odd-sized boards, parity of inversions (excluding blank) must be even.
  const tiles = board.filter((v) => v !== TILES - 1);
  let inv = 0;
  for (let i = 0; i < tiles.length; i++) {
    for (let j = i + 1; j < tiles.length; j++) {
      if (tiles[i] > tiles[j]) inv++;
    }
  }
  return inv % 2 === 0;
};

const shuffle = () => {
  while (true) {
    const arr = Array.from({ length: TILES }, (_, i) => i);
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    if (isSolvable(arr) && !isSolved(arr)) return arr;
  }
};

const PuzzleAct = ({ onNext }) => {
  const { addScore, clearLevel, addAchievement } = useGame();
  const [photoIdx, setPhotoIdx] = useState(0);
  const [board, setBoard] = useState(shuffle);
  const [moves, setMoves] = useState(0);
  const [time, setTime] = useState(0);
  const [solved, setSolved] = useState(false);
  const [phase, setPhase] = useState("ready"); // ready | playing | done
  const [puzzlesDone, setPuzzlesDone] = useState(0);
  const [scoreEarned, setScoreEarned] = useState(0);
  const [hintOpen, setHintOpen] = useState(false);
  const tickRef = useRef(0);

  /* timer */
  useEffect(() => {
    if (phase !== "playing") return;
    const id = setInterval(() => setTime((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const photo = PUZZLE_PHOTOS[photoIdx];

  const blankIdx = board.indexOf(TILES - 1);
  const [bx, by] = idxToXY(blankIdx);

  const tryMove = (i) => {
    if (phase !== "playing" || solved) return;
    const [tx, ty] = idxToXY(i);
    const dx = Math.abs(tx - bx);
    const dy = Math.abs(ty - by);
    if (dx + dy !== 1) return;
    const next = board.slice();
    [next[i], next[blankIdx]] = [next[blankIdx], next[i]];
    setBoard(next);
    setMoves((m) => m + 1);
    SFX.click();
    if (isSolved(next)) {
      setSolved(true);
      const timeBonus = Math.max(0, TIME_BONUS_BASE - time * 3);
      const movePenalty = Math.min(150, moves * MOVE_PENALTY);
      const earned = 100 + timeBonus - movePenalty;
      setScoreEarned(earned);
      addScore(earned);
      SFX.win();

      const newDone = puzzlesDone + 1;
      setPuzzlesDone(newDone);
      if (newDone >= PUZZLE_PHOTOS.length) {
        clearLevel("puzzle", PUZZLE_PHOTOS.length);
        addAchievement("puzzler", "Memory Architect", `Solved all ${PUZZLE_PHOTOS.length} photos`);
        if (moves <= 25) {
          addAchievement("efficient", "Efficient Mind", `Solved in ≤25 moves`);
        }
      }
    }
  };

  const next = () => {
    setPhotoIdx((i) => Math.min(PUZZLE_PHOTOS.length - 1, i + 1));
    setBoard(shuffle());
    setMoves(0);
    setTime(0);
    setSolved(false);
    setScoreEarned(0);
  };

  // Gentle escape hatch: snap board to solved, award small score, advance.
  const autoSolve = () => {
    if (phase !== "playing" || solved) return;
    SFX.click();
    setBoard(Array.from({ length: TILES }, (_, i) => i));
    setSolved(true);
    const earned = 50;
    setScoreEarned(earned);
    addScore(earned);
    SFX.win();

    const newDone = puzzlesDone + 1;
    setPuzzlesDone(newDone);
    if (newDone >= PUZZLE_PHOTOS.length) {
      clearLevel("puzzle", PUZZLE_PHOTOS.length);
    }
  };

  const start = () => {
    resumeAudio();
    SFX.click();
    setBoard(shuffle());
    setMoves(0);
    setTime(0);
    setSolved(false);
    setPuzzlesDone(0);
    setPhotoIdx(0);
    setScoreEarned(0);
    setPhase("playing");
  };

  return (
    <motion.div
      className="md-act md-act-game md-act-puzzle"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
    >
      <HUD level={2} levelLabel="Photo Puzzle" timer={time} />
      <AchievementPopup />

      <div className="md-puzzle-stage">
        {phase === "playing" && (
          <GameControls
            onSkip={onNext}
            controls={[
              { label: "Click any tile that's next to the empty square — it slides over." },
              { label: "Goal: rebuild the photo on the right." },
              { label: "Score: +100 base + time bonus − 5 per move." },
              { label: "3 photos in a row, harder each time." },
            ]}
          />
        )}
        <div className="md-puzzle-meta">
          <div className="md-pill">Puzzle {Math.min(puzzlesDone + 1, PUZZLE_PHOTOS.length)} / {PUZZLE_PHOTOS.length}</div>
          <div className="md-pill">Moves: {moves}</div>
          {scoreEarned > 0 && <div className="md-pill md-pill-gold">+{scoreEarned}</div>}
        </div>

        <div className="md-puzzle-wrap">
          <div className={`md-puzzle-board ${solved ? "md-solved" : ""}`}>
            {board.map((tile, i) => {
              const [tx, ty] = idxToXY(i);
              const [origX, origY] = idxToXY(tile);
              const isBlank = tile === TILES - 1 && !solved;
              return (
                <button
                  key={i}
                  className={`md-tile ${isBlank ? "md-tile-blank" : ""}`}
                  onClick={() => tryMove(i)}
                  disabled={isBlank}
                  aria-label={`tile ${tile + 1}`}
                  style={
                    isBlank
                      ? {}
                      : {
                          backgroundImage: `url(${photo})`,
                          backgroundSize: `${SIZE * 100}% ${SIZE * 100}%`,
                          backgroundPosition: `${(origX / (SIZE - 1)) * 100}% ${(origY / (SIZE - 1)) * 100}%`,
                        }
                  }
                />
              );
            })}
          </div>

        </div>

        <div className="md-puzzle-reference">
          <button
            type="button"
            className="md-puzzle-thumb-btn"
            onClick={() => setHintOpen(true)}
            aria-label="tap to view full original photo"
          >
            <span
              className="md-puzzle-thumb"
              style={{ backgroundImage: `url(${photo})` }}
            />
            <span className="md-puzzle-thumb-label">tap for hint ↗</span>
          </button>
          {phase === "playing" && !solved && (
            <button
              type="button"
              className="md-puzzle-solve-btn"
              onClick={autoSolve}
              aria-label="solve this puzzle automatically"
              title="Reveal the photo and move on (small score, no achievement)"
            >
              ✦ solve for me
            </button>
          )}
        </div>

        <AnimatePresence>
          {hintOpen && (
            <motion.div
              className="md-hint-lightbox"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setHintOpen(false)}
            >
              <motion.img
                src={photo}
                alt="original"
                className="md-hint-image"
                initial={{ scale: 0.85 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.85 }}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                className="md-btn-soft md-hint-close"
                onClick={() => setHintOpen(false)}
              >got it ✓</button>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {solved && phase === "playing" && (
            <motion.div
              className="md-game-toast"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
            >
              <div className="md-toast-title">Solved! +{scoreEarned}</div>
              {puzzlesDone < PUZZLE_PHOTOS.length ? (
                <button className="md-btn-soft md-cta" onClick={next}>Next photo →</button>
              ) : (
                <button className="md-btn-soft md-cta" onClick={() => setPhase("done")}>
                  All {PUZZLE_PHOTOS.length} done ✓
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {phase === "ready" && (
            <motion.div className="md-game-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="md-game-card">
                <div className="md-game-badge">Level 2</div>
                <h2>Photo Puzzle</h2>
                <p className="md-game-tagline">
                  Three pieces of memory, scrambled.<br />
                  Slide them back into place. Fewer moves = bigger score.
                </p>
                <ul className="md-game-rules md-rules-puzzle">
                  <li><strong>+100</strong> base · <strong>+timeBonus</strong> · <strong>−5/move</strong></li>
                  <li>3 puzzles in a row, increasing difficulty</li>
                  <li>Achievement: solve in ≤ 25 moves</li>
                </ul>
                <div className="md-game-actions">
                  <button className="md-btn-soft md-cta" onClick={start}>Start Level 2 →</button>
                  <button className="md-btn-soft md-btn-ghost" onClick={onNext}>Skip</button>
                </div>
              </div>
            </motion.div>
          )}

          {phase === "done" && (
            <motion.div className="md-game-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="md-game-card md-game-win">
                <div className="md-game-badge md-badge-gold">All puzzles solved</div>
                <h2>Three memories restored ❤</h2>
                <p className="md-game-aven-says">
                  ‘See Mama, I remembered every part of you.’
                </p>
                <button className="md-btn-soft md-cta" onClick={onNext}>Next: Level 3 →</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default PuzzleAct;
